/**
 * The help, one source for web and app.
 *
 * Rules for whoever edits this file (CLAUDE.md, rule 37):
 *   - State only what the system does. Every sentence was checked against the
 *     code; a new sentence is checked the same way.
 *   - Never spell out a label, a status, a limit or a period: quote it with a
 *     placeholder (`{{tx.create.submitMember}}`, `{{limits.maxPhotosPerDamage}}`,
 *     facts.ts). help.test.ts fails on a placeholder that resolves to nothing.
 *   - Plain German, du-Form, short sentences. Say where something is, then
 *     what it does.
 *   - A user-visible change updates the topic AND appends an announcement
 *     (ANNOUNCEMENTS in @fsv/core, words in src/news/texts.ts) in the same
 *     change. features.ts maps every screen, route and data operation to a
 *     topic; help.lock.json records the facts each topic was reviewed against.
 */
import type { HelpTopic } from './model';

export const HELP_TOPICS: readonly HelpTopic[] = [
  // ------------------------------------------------------------------ start
  {
    id: 'erste-schritte',
    title: 'Erste Schritte',
    summary: 'Was {{de.app.name}} ist und wie eine Meldung zur Technik kommt.',
    group: 'start',
    keywords: ['Überblick', 'Einstieg', 'was ist'],
    blocks: [
      {
        p: '{{de.app.name}} ist das Schadensregister deines Vereins: Für jedes Flugzeug zeigt eine Risszeichnung, wo schon Kratzer, Beulen oder Lackschäden bekannt sind. So siehst du am Flugzeug in Sekunden, ob etwas neu ist.',
      },
      {
        steps: [
          'Unter {{de.nav.fleet}} wählst du ein Flugzeug.',
          'Auf dem Lageplan siehst du die bekannten Schäden als Punkte. Tippst du auf eine freie Stelle, beginnt eine Meldung.',
          'Bevor du etwas einträgst, zeigt dir die App, was in der Nähe schon erfasst ist.',
          'Deine Meldung erscheint als „{{status.gemeldet}}“, bis die Technik sie gesichtet hat.',
        ],
      },
      {
        p: 'Was du tun darfst, hängt von deiner Rolle im Verein ab: {{role.member}}, {{role.editor}} oder {{role.admin}}.',
      },
    ],
    see: ['rolle-mitglied', 'rolle-technik', 'rolle-verwaltung', 'demo'],
  },
  {
    id: 'anmelden',
    title: 'Anmelden und abmelden',
    summary: 'Mit einem Code per E-Mail, ganz ohne Passwort.',
    group: 'start',
    keywords: ['Login', 'Passwort', 'Code', 'Vereinsflieger', 'abmelden', 'Sitzung'],
    blocks: [
      { p: 'Es gibt keine Passwörter. Du meldest dich mit einem Code an, den du per E-Mail bekommst:' },
      {
        steps: [
          'Gib die E-Mail-Adresse ein, die dein Verein für dich hinterlegt hat, und tippe auf „{{de.auth.requestCode}}“.',
          'Du bekommst eine E-Mail mit einem Code aus {{limits.loginCodeLength}} Ziffern. Er gilt {{limits.loginCodeMinutes}} Minuten.',
          'Gib den Code ein und tippe auf „{{de.auth.verify}}“.',
        ],
      },
      {
        note: 'Aus Datenschutzgründen antwortet die Anmeldung immer gleich: „{{de.auth.codeSentGeneric}}“ – auch wenn die Adresse nicht hinterlegt ist. Kommt nichts an, lies „Der Code kommt nicht an“.',
      },
      {
        p: 'Nach {{limits.loginCodeResendSeconds}} Sekunden kannst du „{{de.auth.resend}}“. Pro Adresse gehen höchstens {{limits.loginCodesPerWindow}} Codes in {{limits.loginCodeWindowMinutes}} Minuten und {{limits.loginCodesPerDay}} am Tag raus; ein Code erlaubt {{limits.loginCodeAttempts}} Versuche.',
      },
      {
        p: 'Hast du mehr als eine Adresse hinterlegt, geht jede davon; der Code kommt an die, die du eingibst (siehe „{{tx.profile.emails.title}}“).',
      },
      {
        p: 'Eine Anmeldung mit dem Vereinsflieger-Zugang gibt es nicht mehr: Vereinsflieger bietet keine Anmeldung für andere Anwendungen an, und dein Vereinsflieger-Passwort geht {{de.app.name}} nichts an. Deine Adresse kommt trotzdem oft aus Vereinsflieger – die Verwaltung deines Vereins übernimmt die Mitgliederliste von dort.',
      },
      { h: 'Angemeldet bleiben und abmelden' },
      {
        p: 'Eine Anmeldung hält {{limits.sessionDays}} Tage und verlängert sich, solange du die App benutzt. Unter {{de.nav.profile}} beendest du sie mit „{{de.auth.signOut}}“; andere Handys und Browser bleiben dann angemeldet.',
      },
      {
        p: '„{{de.auth.signOutEverywhere}}“ beendet jede deiner Anmeldungen auf einmal – auf diesem Gerät, auf anderen Handys und in jedem Browser – und schaltet die Benachrichtigungen auf all diesen Handys ab. Das ist für den Fall gedacht, dass ein Gerät verloren ging oder jemand anderes es benutzt. Danach meldest du dich überall neu an.',
      },
    ],
    see: ['code-kommt-nicht-an', 'kein-verein', 'vereine-wechseln', 'weitere-adressen'],
  },
  {
    id: 'rundgang',
    title: '{{de.welcome.restart}}',
    summary: 'Die App an einem Beispielverein ausprobieren, ohne die Daten deines Vereins zu berühren.',
    group: 'start',
    keywords: ['Rundgang', 'Tour', 'Einführung', 'Willkommen', 'erste Schritte', 'ausprobieren', 'Beispiel'],
    blocks: [
      {
        p: 'Nach deiner ersten Anmeldung fragt die App einmal: „{{de.welcome.title}}“. „{{de.welcome.start}}“ öffnet den Beispielverein Musterstadt mit der {{tx.tour.label}} für deine Rolle – die höchste, die du in einem deiner Vereine hast. „{{de.welcome.skip}}“ schließt die Frage; sie kommt auf keinem Gerät wieder.',
      },
      {
        p: 'Im Rundgang darfst du alles ausprobieren: Schäden melden, Rückfragen/Hinweise schreiben, den Status ändern. Das bleibt auf deinem Gerät; die Daten deines Vereins sieht und ändert der Rundgang nicht.',
      },
      {
        on: ['web'],
        blocks: [{ p: '„{{de.welcome.back}}“ im Band oben bringt dich zurück in deinen Verein, du bleibst angemeldet. Später startest du den Rundgang unter {{de.nav.profile}} → „{{de.welcome.restart}}“.' }],
      },
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [{ p: '„{{de.welcome.back}}“ im Band oben bringt dich zurück in deinen Verein, du bleibst angemeldet. Später startest du den Rundgang unter {{de.nav.profile}} → „{{de.welcome.restart}}“.' }],
      },
    ],
    see: ['demo', 'schaden-melden'],
  },
  {
    id: 'demo',
    title: 'Demo und Kennenlernmodus',
    summary: 'Ohne Konto ausprobieren, mit geführter Tour für jede Rolle.',
    group: 'start',
    keywords: ['ausprobieren', 'Tour', 'Beispiel', 'testen', 'Kennenlernen'],
    blocks: [
      {
        p: 'Auf der Anmeldeseite startet „{{tx.login.demoButton}}“ eine Demo mit erfundenen Vereinen und Flugzeugen. Sie braucht weder Konto noch Netz; was du dort änderst, bleibt auf deinem Gerät.',
      },
      { list: ['Als {{role.member}}: melden und Rückfragen/Hinweise schreiben.', 'Als {{role.editor}}: Meldungen sichten und Status pflegen.', 'Als {{role.admin}}: Mitglieder, Flotte und Einstellungen verwalten.'] },
      { h: '{{tx.tour.label}}' },
      {
        p: 'Beim ersten Öffnen einer Rolle führt dich eine Tour durch die wichtigsten Schritte und hebt dabei die passenden Stellen hervor. Du kannst mit „{{tx.tour.next}}“ und „{{tx.tour.back}}“ blättern oder mit „{{tx.tour.skip}}“ aufhören; die Tour merkt sich je Rolle, wie weit du warst.',
      },
      {
        on: ['web'],
        blocks: [{ p: 'Neu starten oder fortsetzen: über „{{tx.tour.label}}“ im Demo-Band oben oder hier in der Hilfe.' }],
      },
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [{ p: 'Neu starten oder fortsetzen: unter {{de.nav.profile}} → „{{tx.profile.demoRole}}“ oder hier in der Hilfe.' }],
      },
      {
        p: '„{{tx.demo.reset}}“ stellt die Beispieldaten wieder her, „{{tx.demo.end}}“ beendet die Demo. Die Rolle wechselst du unter {{de.nav.profile}} → „{{tx.profile.demoRole}}“.',
      },
    ],
    see: ['erste-schritte'],
  },

  // ------------------------------------------------------------------ tasks
  {
    id: 'flotte',
    title: 'Flotte',
    summary: 'Alle Flugzeuge des Vereins mit offenen Schäden auf einen Blick.',
    group: 'tasks',
    keywords: ['Flugzeuge', 'Übersicht', 'Kennzeichen', 'Startseite'],
    blocks: [
      {
        p: 'Unter {{de.nav.fleet}} steht jedes Flugzeug als Karte: Kennzeichen, Muster, eine kleine Draufsicht mit den offenen Schäden, die Anzahl der offenen Schäden und die letzte Meldung. {{tx.fleet.subtitle}}',
      },
      {
        p: 'Offen sind Schäden mit dem Status „{{status.gemeldet}}“ oder „{{status.bekannt}}“ – dieselben, die der Lageplan zu Beginn zeigt. „{{status.beobachtung}}“ und „{{status.in_reparatur}}“ zählt die Karte nicht; im Lageplan blendest du sie über den Filter ein. Schäden aus anderen Ansichten als der Draufsicht zählt die Karte als „+ … in anderen Ansichten“.',
      },
      { on: ['ipad'], blocks: [{ p: 'Auf dem iPad stehen so viele Karten nebeneinander, wie Platz haben – im Querformat mehr als im Hochformat.' }] },
    ],
    see: ['lageplan', 'status'],
  },
  {
    id: 'lageplan',
    title: 'Lageplan: Ansichten und Zoom',
    summary: 'Die Risszeichnung mit allen Schäden, in sieben Ansichten (auch innen) und bis {{limits.maxZoom}}-fach vergrößert.',
    group: 'tasks',
    keywords: ['Zeichnung', 'Riss', 'Pins', 'zoomen', 'vergrößern', 'Ansicht', 'gespiegelt', 'Legende', 'Liste', 'Innenraum', 'Kabine', 'Sitz', 'Instrumentenbrett'],
    blocks: [
      {
        p: 'Jeder Schaden ist ein Punkt auf der Zeichnung. Farbe und Form zeigen den Status, so bleibt er auch in der Sonne erkennbar. Liegen mehrere Schäden dicht beieinander, fasst die Zeichnung sie zu einem Kreis mit Zahl zusammen; antippen zeigt die Liste.',
      },
      { h: '{{tx.plan.viewSwitcher}}' },
      {
        p: 'Oben wählst du zwischen {{view.oben}}, {{view.unten}}, {{view.links}}, {{view.rechts}}, {{view.vorne}} und {{view.innen}}. {{view.unten}} und {{view.rechts}} sind meist gespiegelte Zeichnungen der Gegenseite – das steht dann als „{{tx.plan.mirrored}}“ dabei.',
      },
      {
        p: 'Eine Ansicht von hinten gibt es nicht: Das Heck läuft spitz zu, Schäden dort meldest du von {{view.oben}}, {{view.unten}}, {{view.links}} oder {{view.rechts}} aus.',
      },
      {
        p: 'Die Zahl an jeder Ansicht sagt, wie viele Schäden dort zu sehen sind – genau die Status, die der Filter gerade zeigt. Ihre Farbe ist die des dringendsten: {{status.gemeldet}} vor {{status.bekannt}}, dann {{status.in_reparatur}} und {{status.beobachtung}}. Ansichten ohne Schaden bleiben ohne Zahl. Der Lageplan öffnet auf der Ansicht mit den meisten Schäden.',
      },
      {
        p: '{{view.innen}} zeigt die Kabine von oben, Nase oben: Instrumentenbrett, Sitze und Gepäckraum. Solange der Verein keine eigene Innenraum-Zeichnung hochgeladen hat, steht dort ein Schema als Platzhalter; Schäden setzt du darauf wie außen.',
      },
      { h: 'Vergrößern' },
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [{ p: '{{mt.plan.pinchHint}} Mit + und − geht es auch per Knopf, ⤢ zeigt wieder die ganze Zeichnung.' }],
      },
      {
        on: ['web'],
        blocks: [
          {
            list: [
              'Am Handy: mit einem Finger verschieben, mit zwei Fingern zoomen. Antippen setzt das Kreuz für eine Meldung; Finger gedrückt halten zeigt die Lupe.',
              'Am Computer: Mausrad zum Zoomen, Ziehen zum Verschieben.',
              'Die Knöpfe „{{tx.plan.zoomIn}}“ (+), „{{tx.plan.zoomOut}}“ (−) und „{{tx.plan.zoomReset}}“ (⤢) gibt es überall.',
              'Mit der Tastatur: {{tx.plan.keyboardHint}}',
            ],
          },
        ],
      },
      { h: 'Filter, Legende und Liste' },
      {
        on: ['web'],
        blocks: [
          {
            p: 'Unter der Zeichnung blendest du Status ein und aus; zu Beginn sind {{status.gemeldet}} und {{status.bekannt}} eingeschaltet; „{{tx.plan.legend}}“ erklärt die Formen. „{{tx.plan.listToggle}}“ zeigt alle Schäden als Liste – wahlweise „{{tx.plan.thisView}}“ oder „{{tx.plan.allViews}}“. Auf großen Bildschirmen stehen Zeichnung und Liste nebeneinander.',
          },
        ],
      },
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [
          { p: 'Unter der Zeichnung blendest du Status ein und aus; zu Beginn sind {{status.gemeldet}} und {{status.bekannt}} eingeschaltet. Neben jedem Status steht, wie viele es in dieser Ansicht gibt.' },
          { p: 'Darunter öffnet „{{tx.plan.listToggle}} ›“ die Schäden dieser Ansicht als Liste.' },
        ],
      },
      { p: 'Verworfene Schäden zeigt die Zeichnung nicht, behobene blass.' },
    ],
    see: ['schaden-melden', 'status'],
  },
  {
    id: 'hinweise-der-technik',
    title: 'Hinweise der Technik',
    summary: 'Was die Technik zu einem Flugzeug wissen lässt, bevor du fliegst.',
    group: 'tasks',
    keywords: ['INOP', 'gesperrt', 'Einschränkung', 'gelesen', 'flugtauglich', 'Sperre', 'Hinweis'],
    blocks: [
      {
        p: 'Öffnest du ein Flugzeug, stehen ganz oben die {{de.notices.title}}: Dinge, die du vor dem Flug wissen solltest und die kein Schaden sind – etwa eine laufende Fehlersuche oder ein ausgefallenes Gerät. In der {{de.nav.fleet}} trägt ein Flugzeug mit Hinweisen ein Zeichen mit ihrer Zahl.',
      },
      {
        list: [
          '„{{notice.info}}“: {{de.notices.levelHint.info}}',
          '„{{notice.einschraenkung}}“: {{de.notices.levelHint.einschraenkung}}',
          '„{{notice.gesperrt}}“ (nur wenn der Verein es eingeschaltet hat): {{de.notices.levelHint.gesperrt}} Ein gesperrtes Flugzeug ist überall rot markiert.',
        ],
      },
      { p: 'Hast du einen Hinweis gelesen, tippe auf „{{de.notices.acknowledge}}“. Die Technik sieht, wer ihn gelesen hat. Ändert sie später, was dort steht, fragt die App dich erneut.' },
      { p: 'Einen Schaden an einem gesperrten Flugzeug meldest du trotzdem wie gewohnt; die Meldung zeigt dann oben die Sperre an. {{de.notices.reportWarningHint}}' },
      { h: 'Für die Technik' },
      {
        p: '{{role.editor}} und {{role.admin}} legen Hinweise mit „{{de.notices.add}}“ an, ändern sie und heben sie mit „{{de.notices.resolve}}“ auf. Ein aufgehobener Hinweis verschwindet für alle und bleibt unter „{{de.notices.history}}“ und im {{de.admin.protocol}} erhalten. Optional gilt ein Hinweis nur bis zu einem Tag ({{de.notices.validUntilHint}}).',
      },
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [
          {
            p: 'In der App geht das genauso, direkt am Flugzeug: oben im Lageplan unter den {{de.notices.title}}. Den letzten Tag tippst du als {{mt.notices.dayPlaceholder}} ein. {{mt.notices.offline}}',
          },
        ],
      },
      { note: 'Es gibt keine Benachrichtigung für Hinweise: Sie stehen dort, wo du vor dem Flug ohnehin nachsiehst.' },
    ],
    see: ['lageplan', 'einstellungen', 'flotte'],
  },
  {
    id: 'schaden-melden',
    title: 'Schaden melden',
    summary: 'Stelle antippen, Nähe-Warnung prüfen, beschreiben, Fotos dazu.',
    group: 'tasks',
    roles: ['member', 'editor', 'admin'],
    keywords: ['melden', 'erfassen', 'neu', 'anlegen', 'Kratzer', 'Beule'],
    blocks: [
      {
        steps: [
          'Öffne unter {{de.nav.fleet}} das Flugzeug und wähle die passende Ansicht.',
          'Auf den Ansichten {{view.oben}} und {{view.unten}} fragt die App zuerst „{{tx.plan.standpoint.question}}“. Stehst du vor dem Flugzeug, wähle „{{tx.plan.standpoint.nose}}“: Die Zeichnung dreht sich ({{tx.plan.standpoint.watermark}}), so dass links und rechts zu dem passen, was du vor dir siehst. Gespeichert wird der Schaden trotzdem an der richtigen Stelle; alle anderen sehen ihn wie gewohnt. Die Frage kommt bei jeder Meldung neu, „{{tx.plan.standpoint.change}}“ stellt sie sofort noch einmal.',
          'Tippe auf die Stelle, an der der Schaden ist; mit der Maus genügt ein Klick. Genauer geht es, wenn du den Finger kurz gedrückt hältst: Über dem Finger erscheint eine Lupe, die zeigt, wo das Kreuz landet; verschieb es, bis es sitzt, und lass los. Wird es eng, zoome vorher mit zwei Fingern heran. Ein Finger, den du gleich bewegst, verschiebt die Zeichnung.',
          'Sitzt das Kreuz nicht, halte es gedrückt und zieh es an die richtige Stelle, oder tippe woanders neu; „{{tx.plan.placeDiscard}}“ entfernt es. Passt es, tippe auf „{{tx.plan.placeConfirm}}“ (als Technik: „{{tx.plan.placeConfirmEditor}}“).',
          'Steht dort schon etwas, zeigt die App „{{tx.create.nearbyTitle}}“ mit den Einträgen in der Nähe. Ist es derselbe Schaden, öffne ihn mit „{{tx.create.toExisting}}“; sonst weiter mit „{{tx.create.newAnyway}}“.',
          'Wähle die Kategorie ({{category.lack}}, {{category.kratzer}}, {{category.beule}}, {{category.riss}} oder {{category.sonstiges}}) – passt mehr als eine, tippe alle an, etwa {{category.kratzer}} und {{category.lack}}. Gib dann einen Titel mit {{limits.titleMin}} bis {{limits.titleMax}} Zeichen ein. Die Beschreibung ist freiwillig.',
          'Füge Fotos hinzu: {{tx.create.photoHint}} Hat dein Verein die {{de.media.requiredTitle}} eingeschaltet, lässt sich die Meldung erst mit mindestens einem Foto oder Video absenden.',
          'Zeigt ein kurzer Schwenk mehr als ein Foto, nimm ein Video auf: „{{tx.video.add}}“. Höchstens {{limits.videoMaxSeconds}} Sekunden und {{limits.videoMaxPerDamage}} Videos je Schaden. Das Video wird auf dem Gerät verkleinert – der Balken zeigt, wie weit –, der Aufnahmeort wird entfernt. Der Ton bleibt: Du kannst den Schaden beim Filmen beschreiben.',
          'Tippe auf „{{tx.create.submitMember}}“ (Technik und Verwaltung: „{{tx.create.submitEditor}}“).',
        ],
      },
      { note: '{{tx.create.memberStatusNote}}' },
      {
        p: 'Technik und Verwaltung wählen beim Erfassen gleich den Status. Bis zur Sichtung kannst du als {{role.member}} Titel und Beschreibung deiner Meldung noch ändern, eigene Fotos entfernen oder sie mit „{{tx.detail.withdraw}}“ zurückziehen. Fotos und Videos ergänzen kannst du auch danach mit „{{tx.detail.addPhotos}}“.',
      },
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [
          {
            p: 'Ohne Verbindung wird die Meldung trotzdem gespeichert und erscheint mit gestricheltem Punkt als „{{mt.outbox.pending}}“. Die App schickt sie, sobald wieder Netz da ist.',
          },
        ],
      },
      { on: ['web'], blocks: [{ p: 'Im Browser geht Melden nur mit Verbindung; ohne Netz erscheint „{{de.errors.offline}}“.' }] },
      { p: 'Ob Mitglieder melden dürfen, legt der Verein fest. Ist es abgeschaltet, steht unter der Zeichnung: „{{de.participation.reportOff}}“' },
    ],
    see: ['naehe-warnung', 'fotos', 'meine-meldungen', 'offline', 'mitwirkung'],
  },
  {
    id: 'naehe-warnung',
    title: 'Nähe-Warnung: Ist das schon bekannt?',
    summary: 'Vor jeder Meldung zeigt die App, was an dieser Stelle schon erfasst ist.',
    group: 'tasks',
    keywords: ['doppelt', 'Duplikat', 'schon bekannt', 'Nähe', 'Radius'],
    blocks: [
      {
        p: 'Tippst du auf die Zeichnung, sucht die App im Umkreis nach erfassten Schäden – ohne Netz, mit den Daten, die schon geladen sind. Findet sie welche, kommt zuerst „{{tx.create.nearbyTitle}}“: {{tx.create.nearbyIntro}}',
      },
      {
        list: [
          '„{{tx.create.toExisting}}“ öffnet den Eintrag. Dort kannst du eine Rückfrage/einen Hinweis schreiben, statt doppelt zu melden.',
          '„{{tx.create.newAnyway}}“ führt zum Formular, wenn es wirklich etwas anderes ist.',
          'Technik und Verwaltung sehen zusätzlich „{{de.damage.sameDamage}}“: Die neuen Fotos landen dann beim bestehenden Schaden, es entsteht kein neuer Eintrag.',
        ],
      },
      {
        p: 'Angezeigt werden alle Status außer „{{status.verworfen}}“; „{{status.behoben}}“ bleibt dabei, weil ein neuer Kratzer an alter Stelle wichtig ist. Den Umkreis stellt die Verwaltung ein (Start: {{settings.duplicateRadiusPercent}} % der Zeichnungsbreite).',
      },
    ],
    see: ['schaden-melden', 'einstellungen'],
  },
  {
    id: 'fotos',
    title: 'Fotos',
    summary: 'Bis zu {{limits.maxPhotosPerDamage}} Fotos je Schaden, verkleinert und ohne Standortdaten.',
    group: 'tasks',
    keywords: ['Bild', 'Kamera', 'Galerie', 'Mediathek', 'Übersichtsfoto', 'EXIF', 'Standort'],
    blocks: [
      {
        list: [
          'Höchstens {{limits.maxPhotosPerDamage}} Fotos je Schaden.',
          '„{{tx.create.addPhotoCamera}}“ öffnet die Kamera, „{{tx.create.addPhotoGallery}}“ deine Fotos.',
          'Empfohlen: ein Übersichtsfoto aus 1–2 m Abstand, damit man die Stelle am Flugzeug wiederfindet, und ein Detailfoto. Den Stern „{{tx.create.overviewToggle}}“ setzt du beim Übersichtsfoto.',
        ],
      },
      {
        p: 'Jedes Foto wird auf deinem Gerät auf {{limits.photoEdgePx}} Pixel verkleinert, dazu entsteht ein Vorschaubild. Dabei fallen Ortsangaben und Kameradaten weg. Der Server prüft das noch einmal und nimmt kein Foto mit solchen Daten und keines über {{limits.maxPhotoMegabytes}} MB an.',
      },
      {
        p: 'Fotos und Videos ergänzt du als {{role.member}} bei deiner eigenen Meldung jederzeit – auch nachdem die Technik sie gesichtet hat: im Schaden „{{tx.detail.addPhotos}}“. Die Technik kann das bei jedem Schaden. Eigene Fotos entfernen kannst du als {{role.member}} nur bis zur Sichtung; ein entferntes Foto landet im {{de.admin.trash}}.',
      },
      {
        on: ['web'],
        blocks: [{ p: 'Im Browser lädt die App die Fotos nacheinander hoch. Wird eines abgelehnt, bleibt es markiert im Formular, die anderen sind gespeichert: {{tx.upload.retryHint}}' }],
      },
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [
          { p: 'In der App fügst du Fotos beim Melden hinzu oder später im Schaden mit „{{tx.detail.addPhotos}}“. Ohne Netz warten sie in der {{mt.outbox.title}} und gehen raus, sobald eine Verbindung besteht – wie eine Meldung.' },
          { p: 'Fragt das Telefon nach Kamera oder Fotos, erlaube den Zugriff. Wie du ihn später änderst, steht im Abschnitt zu deinem Gerät.' },
        ],
      },
    ],
    see: ['foto-abgelehnt', 'iphone', 'android', 'web'],
  },
  {
    id: 'schaden-detail',
    title: 'Ein Schaden im Detail',
    summary: 'Fotos, Position, Status, Verlauf und Rückfragen/Hinweise eines Schadens.',
    group: 'tasks',
    keywords: ['Detail', 'Schadensnummer', 'Verlauf', 'Lightbox'],
    blocks: [
      {
        p: 'Tippst du auf einen Punkt oder eine Listenzeile, öffnet sich der Schaden: Schadensnummer (Kürzel des Vereins und sechs Ziffern, z. B. ABC-000042), Status, Kategorie, Ansicht, wer ihn wann gemeldet hat, die Fotos und eine kleine Zeichnung mit seiner Position.',
      },
      { list: ['Fotos öffnen sich groß; mit den Pfeilen oder durch Wischen blätterst du.', '„{{tx.detail.openPlan}}“ springt zur Stelle auf dem Lageplan.', 'Wurde die Meldung gesichtet, steht dabei, von wem und wann – bei einer zusammengeführten Meldung auch, in welchem Schaden sie aufgegangen ist.'] },
      { p: 'Unten stehen die {{de.damage.comments}} und der {{de.damage.history}} mit jeder Änderung.' },
    ],
    see: ['rueckfragen', 'verlauf', 'status-aendern'],
  },
  {
    id: 'rueckfragen',
    title: 'Rückfragen/Hinweise',
    summary: 'Fragen und Hinweise zu einem Schaden, sichtbar für den ganzen Verein.',
    group: 'tasks',
    keywords: ['Kommentar', 'Frage', 'Hinweis', 'Nachricht'],
    blocks: [
      {
        p: 'Unter „{{de.damage.comments}}“ schreibst du im Schaden eine Frage oder einen Hinweis (bis {{limits.commentMax}} Zeichen) und tippst auf „{{tx.detail.commentSend}}“. Alle Mitglieder des Vereins können sie lesen.',
      },
      { p: 'Das ist der richtige Weg, wenn du einen bekannten Schaden anders siehst – etwa größer als beschrieben –, statt ihn neu zu melden.' },
      { p: 'Ob Mitglieder Rückfragen/Hinweise schreiben, legt der Verein fest. Ist es abgeschaltet, steht dort: „{{de.participation.commentOff}}“' },
      {
        p: 'Deine eigenen Rückfragen/Hinweise löschst du mit „{{tx.detail.commentDelete}}“ neben dem Eintrag; {{role.editor}} und {{role.admin}} können jeden löschen. Direkt danach holt „{{de.damage.undo}}“ ihn zurück, später die Verwaltung im {{de.admin.trash}}.',
      },
    ],
    see: ['schaden-detail', 'mitwirkung'],
  },
  {
    id: 'verlauf',
    title: 'Verlauf und Rückgängig',
    summary: 'Jede Änderung bleibt nachvollziehbar; ein Fehlgriff lässt sich sofort zurücknehmen.',
    group: 'tasks',
    keywords: ['rückgängig', 'undo', 'Fehler', 'Historie', 'Änderungen', 'versehentlich'],
    blocks: [
      {
        p: 'Jede Aktion wird sofort gespeichert. Danach zeigt die App {{limits.undoSeconds}} Sekunden lang einen Hinweis mit „{{de.damage.undo}}“. Tippst du darauf, wird die Aktion durch eine Gegenbuchung zurückgenommen.',
      },
      {
        p: 'Im Schaden steht unter „{{de.damage.history}}“ jede Änderung mit Person und Zeit – auch zurückgenommene. Gelöscht wird nichts endgültig: Gelöschtes landet im {{de.admin.trash}}.',
      },
      { note: 'Ist die Zeit abgelaufen oder hat inzwischen jemand anderes etwas geändert, erscheint „{{tx.toast.undoFailed}}“. Dann änderst du den Schaden einfach wieder zurück.' },
    ],
    see: ['papierkorb', 'protokoll'],
  },
  {
    id: 'suche',
    title: 'Suche',
    summary: 'Schäden über alle Flugzeuge finden – nach Text, Nummer oder Kennzeichen.',
    group: 'tasks',
    keywords: ['finden', 'Filter', 'Schadensnummer', 'Kennzeichen'],
    blocks: [
      {
        p: 'Unter {{de.nav.search}} findest du jeden Schaden deines Vereins. {{tx.search.minChars}} Die Schadensnummer findest du auch abgekürzt: für ABC-000042 genügt „42“.',
      },
      {
        on: ['web'],
        blocks: [
          {
            p: 'Unter „{{tx.search.filters}}“ grenzt du nach Flugzeug, Kategorie (ein Schaden mit mehreren Kategorien erscheint bei jeder davon), Status, Ansicht und {{tx.search.period}} ein, „{{tx.search.withPhoto}}“ zeigt nur Schäden mit Foto. Die Adresse der Seite enthält die Suche – du kannst sie als Link weitergeben.',
          },
        ],
      },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: 'Unter dem Suchfeld blendest du einzelne Status ein und aus; „{{tx.search.withPhoto}}“ zeigt nur Schäden mit Foto.' }] },
    ],
    see: ['schaden-detail'],
  },
  {
    id: 'meine-meldungen',
    title: 'Meine Meldungen',
    summary: 'Was aus deinen Meldungen geworden ist.',
    group: 'tasks',
    roles: ['member'],
    keywords: ['eigene', 'Status', 'Notiz der Technik'],
    blocks: [
      {
        p: 'Unter {{de.nav.profile}} → „{{tx.profile.myReports}}“ stehen deine Meldungen mit ihrem Status. Hat die Technik eine Notiz geschrieben – etwa warum sie eine Meldung verworfen hat –, steht sie dabei; bei einer zusammengeführten Meldung auch die Nummer des Schadens, in dem sie aufgegangen ist.',
      },
    ],
    see: ['status', 'schaden-melden'],
  },
  {
    id: 'status',
    title: 'Was die Status bedeuten',
    summary: 'Von „{{status.gemeldet}}“ bis „{{status.behoben}}“ – und wer was ändert.',
    group: 'tasks',
    keywords: ['Status', 'Farbe', 'Symbol', 'Legende'],
    blocks: [
      {
        list: [
          '{{status.gemeldet}} (Ring): von einem Mitglied gemeldet, noch nicht gesichtet.',
          '{{status.bekannt}} (Punkt): von der Technik bestätigt.',
          '{{status.beobachtung}} (Punkt mit Auge): wird beobachtet, etwa bis zur nächsten Kontrolle.',
          '{{status.in_reparatur}} (Quadrat): die Reparatur ist beschlossen und wird erledigt; bis dahin bleibt der Schaden, wie er ist.',
          '{{status.akzeptiert}} (Punkt mit Strich, etwas blasser): geprüft und bewusst so belassen – bleibt so, etwa ein Lackschaden. Die Begründung steht im Schaden; er zählt nicht als offen.',
          '{{status.behoben}} (blasser Punkt mit Haken): erledigt, bleibt aber sichtbar.',
          '{{status.verworfen}}: kein Schaden oder doppelt gemeldet; auf der Zeichnung ausgeblendet.',
        ],
      },
      { p: 'Den Status ändern nur Technik und Verwaltung. Eine Meldung von Mitgliedern beginnt immer als „{{status.gemeldet}}“.' },
      {
        p: 'Ob ein Schaden die Lufttüchtigkeit berührt, entscheidet die Technik bzw. der Prüfer, nicht die App. „{{status.akzeptiert}}“ hält diese Entscheidung nur fest, damit jeder beim Außencheck sieht, dass sich jemand darum gekümmert hat.',
      },
    ],
    see: ['status-aendern', 'lageplan'],
  },
  {
    id: 'eingang',
    title: 'Eingang: Meldungen sichten',
    summary: 'Neue Meldungen bestätigen, zusammenführen oder verwerfen.',
    group: 'tasks',
    roles: ['editor', 'admin'],
    keywords: ['sichten', 'bestätigen', 'zusammenführen', 'verwerfen', 'Duplikat', 'überfällig', 'Review'],
    blocks: [
      {
        p: 'Unter {{de.nav.inbox}} stehen alle Meldungen im Status „{{status.gemeldet}}“, die älteste zuerst. Die Zahl am Symbol zeigt, wie viele offen sind. Jede Meldung zeigt Flugzeug, Position, Melder, Alter und wie viele Schäden in der Nähe schon erfasst sind.',
      },
      {
        p: 'Nach {{settings.reviewDueDays}} Tagen ohne Sichtung ist eine Meldung „{{de.review.overdue}}“ und wird hervorgehoben; die Frist stellt die Verwaltung ein.',
      },
      { h: 'Drei Ausgänge' },
      {
        list: [
          '„{{de.review.confirm}}“: Der Schaden gilt als „{{status.bekannt}}“. Willst du vorher Titel, Kategorie oder Beschreibung korrigieren, öffne die Meldung und nimm „{{de.damage.edit}}“.',
          '„{{de.review.merge}}“: Es ist ein Schaden, den es schon gibt. Wähle ihn aus den Einträgen in der Nähe oder gib seine Nummer ein. Fotos und Beschreibung wandern dorthin, die Meldung wird „{{status.verworfen}}“.',
          '„{{de.review.reject}}“: Kein Schaden. Dazu gehört eine Begründung, die der Melder sieht.',
        ],
      },
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [{ p: 'In der App tippst du auf eine Meldung und findest die Knöpfe im Schaden unter „{{tx.detail.reviewActions}}“.' }],
      },
      { p: 'Auch das Sichten lässt sich {{limits.undoSeconds}} Sekunden lang rückgängig machen.' },
    ],
    see: ['status-aendern', 'naehe-warnung', 'rolle-technik'],
  },
  {
    id: 'status-aendern',
    title: 'Status ändern und bearbeiten',
    summary: 'Für Technik und Verwaltung: Schäden pflegen, bis sie behoben sind.',
    group: 'tasks',
    roles: ['editor', 'admin'],
    keywords: ['Status', 'Reparatur', 'behoben', 'wieder öffnen', 'bearbeiten', 'löschen'],
    blocks: [
      {
        p: 'Im Schaden stehen unter „{{tx.detail.reviewActions}}“ nur die Schritte, die vom aktuellen Status aus möglich sind: „{{tx.action.confirm}}“, „{{tx.action.observe}}“, „{{tx.action.repair}}“, „{{tx.action.accept}}“, „{{tx.action.resolve}}“, „{{tx.action.reject}}“ und bei behobenen, verworfenen oder akzeptierten Schäden „{{tx.action.reopen}}“.',
      },
      {
        p: '„{{tx.action.accept}}“ heißt: geprüft, bleibt so. Dazu gehört wie beim Verwerfen eine Begründung; sie steht im Schaden für alle, die ihn antippen. Der Punkt bleibt auf der Zeichnung, damit niemand ihn erneut meldet. Wird doch repariert, geht es mit „{{tx.action.repair}}“ weiter.',
      },
      {
        p: '„{{de.damage.edit}}“ ändert Titel, Beschreibung, Kategorie und Status. „{{tx.detail.deleteDamage}}“ legt den Schaden in den {{de.admin.trash}}; die Verwaltung kann ihn von dort zurückholen.',
      },
      {
        p: 'Sitzt ein Schaden an der falschen Stelle oder auf der falschen Seite, korrigiert „{{tx.detail.move.action}}“ ihn, statt ihn neu anzulegen: Ansicht wählen, auf die richtige Stelle tippen, „{{tx.detail.move.save}}“. Nummer, Fotos, Beschreibung und Rückfragen/Hinweise bleiben; im Verlauf steht „{{tx.history.moved}}“, und „{{de.damage.undo}}“ setzt ihn direkt danach zurück.',
      },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: 'Bearbeiten geht im Browser; „{{tx.detail.addPhotos}}“, Statusschritte, „{{tx.detail.move.action}}“ und Löschen auch in der App. „{{tx.detail.move.action}}“ braucht dort eine Verbindung, Fotos und Videos warten ohne Netz in der Warteschlange.' }] },
    ],
    see: ['status', 'verlauf', 'papierkorb'],
  },
  {
    id: 'mitglieder',
    title: 'Mitglieder verwalten',
    summary: 'Wer sich anmelden darf und mit welcher Rolle.',
    group: 'tasks',
    roles: ['admin'],
    keywords: ['einladen', 'CSV', 'Import', 'Rolle', 'deaktivieren', 'anonymisieren', 'Löschanfrage', 'Sitzungen', 'E-Mail-Adresse', 'Adresse ändern', 'weitere Adresse'],
    blocks: [
      {
        p: 'Unter {{de.nav.admin}} → {{de.admin.members}} steht, wer sich anmelden darf. Nur wer hier eingetragen und aktiv ist, kommt mit dem E-Mail-Code hinein.',
      },
      {
        list: [
          '„{{tx.admin.members.add}}“: Name, E-Mail und Rolle. Die Person bekommt sofort eine Einladung per E-Mail: wer eingeladen hat, wofür {{de.app.name}} da ist und wie die Anmeldung mit dem Code funktioniert, dazu ein Link, der ihre Adresse schon einträgt.',
          '„{{tx.admin.members.csvTitle}}“: {{tx.admin.members.csvHelp}} Vor dem Import zeigt eine Vorschau, wer neu ist, wer sich ändert und – nur mit „{{tx.admin.members.csvDeactivateMissing}}“ – wer deaktiviert würde. Mit „{{tx.admin.members.csvInvite}}“ (vorbelegt) bekommen neu Angelegte dieselbe Einladung, nach und nach: höchstens {{limits.invitationsPerHour}} pro Stunde, damit Anmeldecodes jederzeit durchkommen.',
          'Die Spalte „{{tx.admin.members.invitation}}“ zeigt, ob die Einladung noch {{tx.admin.members.invitationPending}} ist, wann sie verschickt wurde oder ob sie {{tx.admin.members.invitationFailed}} war. „{{tx.admin.members.invite}}“ bzw. „{{tx.admin.members.inviteAgain}}“ schickt sie (noch einmal) – etwa wenn jemand sie nicht findet.',
          'Rolle ändern, „{{tx.admin.members.deactivate}}“ und „{{tx.admin.members.endSessions}}“ wirken bei der nächsten Aktion der Person.',
          '„{{tx.admin.members.changeEmail}}“ korrigiert die Adresse, mit der sich jemand anmeldet. {{tx.admin.members.changeEmailConsequence}} Nimm die Adresse, die auch in Vereinsflieger steht: Der Import erkennt Personen an der Adresse, mit einer anderen käme sie dort als neue Person an. Gehört die Person auch zu einem anderen Verein, geht das nicht – dann ändert sie die Adresse nicht hier.',
          '„{{tx.admin.members.additionalAdd}}“ trägt eine weitere Adresse ein, mit der sich die Person ebenfalls anmelden kann. {{tx.admin.members.additionalHint}} {{tx.admin.members.additionalRemoveConsequence}} Gehört die Person auch zu einem anderen Verein, geht das nicht – dann trägt sie ihre Adressen selbst im Profil ein. Eine vom Verein eingetragene Adresse erkennt die Person nur in diesem Verein: Ein anderer Verein kann sie darüber weder hinzufügen noch importieren, das gelingt nur mit einer Adresse, die die Person selbst bestätigt hat.',
          '„{{tx.admin.members.anonymize}}“ ist für Löschanfragen und lässt sich nicht rückgängig machen. Meldungen und Fotos bleiben beim Verein.',
        ],
      },
      { note: '{{de.admin.lastAdmin}}' },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: '{{mt.web.hint}} „{{mt.web.open}}“ unter {{de.nav.profile}} öffnet sie.' }] },
    ],
    see: ['mitglieder-vereinsflieger', 'rolle-verwaltung', 'protokoll'],
  },
  {
    id: 'mitglieder-vereinsflieger',
    title: 'Mitgliederliste aus Vereinsflieger übernehmen',
    summary: 'Als CSV exportieren, hier importieren, Status wählen.',
    group: 'tasks',
    roles: ['admin'],
    keywords: ['Vereinsflieger', 'CSV', 'Export', 'Import', 'Excel', 'Mitgliederliste', 'Status', 'aktiv', 'passiv', 'Abgleich'],
    blocks: [
      {
        p: 'Eine Anmeldung über Vereinsflieger gibt es nicht, die Mitgliederliste lässt sich aber von dort übernehmen. {{de.app.name}} verbindet sich dabei nie mit Vereinsflieger, und niemand gibt ein Vereinsflieger-Passwort ein.',
      },
      {
        steps: [
          'In Vereinsflieger die Mitgliederliste als CSV-Datei exportieren. Sie braucht mindestens die E-Mail-Adresse und den Namen (Vorname und Nachname, oder Name); eine Spalte Mitgliedsstatus hilft beim Aussortieren.',
          'Hier unter {{de.nav.admin}} → {{de.admin.members}} auf „{{de.admin.csvImport}}“ tippen und die Datei wählen.',
          'Unter „{{tx.admin.members.csvStatusTitle}}“ ankreuzen, welche Werte als Mitglied gelten. Vorbelegt sind alle Werte mit dem Wort aktiv – etwa auch Doppelmitglied aktiv –, nicht aber inaktiv; enthält die Datei keinen, ist nichts angekreuzt. Prüf die Auswahl, bevor du importierst.',
          'In der Vorschau prüfen, wer neu ist, wer sich ändert und wer deaktiviert wird, dann „{{tx.admin.members.csvApply}}“.',
        ],
      },
      {
        list: [
          'Zeilen mit einem anderen Status werden nicht übernommen; wer damit schon Mitglied ist, wird deaktiviert. Du selbst bleibst immer aktiv.',
          'Ohne Rollenspalte werden neue Personen {{role.member}}, vorhandene behalten ihre Rolle. {{role.editor}} und {{role.admin}} vergibst du danach in der Liste.',
          'Zeilen ohne oder mit falscher E-Mail-Adresse und doppelte Adressen stehen mit ihrer Zeilennummer unter „{{tx.admin.members.csvInvalid}}“ und werden übersprungen.',
          'Umlaute aus Excel kommen richtig an: Die Datei darf UTF-8 oder Windows-1252 sein, getrennt mit Semikolon, Komma oder Tabulator.',
        ],
      },
      {
        note: 'Mit „{{tx.admin.members.csvInvite}}“ (vorbelegt) bekommen neu Angelegte eine Einladung per E-Mail. Die Einladungen gehen nach und nach raus, höchstens {{limits.invitationsPerHour}} pro Stunde; bei einem großen Verein dauert das ein paar Stunden. Ohne das Häkchen sag den neuen Mitgliedern selbst, dass sie sich mit ihrer Adresse anmelden können.',
      },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: '{{mt.web.hint}} „{{mt.web.open}}“ unter {{de.nav.profile}} öffnet sie.' }] },
    ],
    see: ['mitglieder', 'anmelden'],
  },
  {
    id: 'flotte-verwalten',
    title: 'Flotte und Muster verwalten',
    summary: 'Flugzeuge anlegen, außer Dienst stellen und Risszeichnungen hochladen.',
    group: 'tasks',
    roles: ['admin'],
    keywords: ['Flugzeug anlegen', 'Muster', 'Zeichnung', 'Kennzeichen', 'außer Dienst', 'PNG', 'hochladen', 'Klasse', 'Monatsgebühr', 'Segelflugzeug', 'Motorsegler', 'UL'],
    blocks: [
      { p: 'Unter {{de.nav.admin}} → {{de.admin.fleet}} gibt es zwei Reiter: „{{tx.admin.fleet.tabAircraft}}“ und „{{tx.admin.fleet.tabTypes}}“.' },
      { h: '{{tx.admin.fleet.tabAircraft}}' },
      {
        list: [
          '„{{tx.admin.fleet.addAircraft}}“ mit Kennzeichen ({{tx.admin.fleet.registrationHint}}) und Muster.',
          'Das Kennzeichen lässt sich nur ändern, solange es zu dem Flugzeug keinen Schaden gibt.',
          'Löschen geht nur ohne Schäden. Sonst: „{{tx.admin.fleet.active}}“ ausschalten – das Flugzeug verschwindet aus der Flotte, seine Geschichte bleibt.',
          'Ein Musterwechsel kann Punkte verrutschen lassen, wenn die neuen Zeichnungen andere Proportionen haben. Die App fragt dann nach („{{tx.admin.fleet.driftTitle}}“).',
        ],
      },
      { h: '{{tx.admin.fleet.tabTypes}}' },
      {
        p: 'Muster aus dem Katalog sind fertig gezeichnet: Diamond DA20 und DA40, Cessna 150, 152 und 172, Piper PA-28, Robin DR400, Cirrus SR22, die Segelflugzeuge DG-300, ASG 29, ASK 21, LS 4 und DG-1000S sowie das UL WT9 Dynamic. Ein eigenes Muster braucht Kürzel, Name, Klasse und Quellenangabe und drei Grundansichten ({{view.oben}}, {{view.vorne}}, {{view.links}}); die übrigen spiegelt die App. {{tx.admin.fleet.fileHint}} {{tx.admin.fleet.incompleteHint}}',
      },
      {
        p: '„{{de.aircraftClass.field}}“: {{de.aircraftClass.label.motor}}, {{de.aircraftClass.label.tmg}}, {{de.aircraftClass.label.glider}} oder {{de.aircraftClass.label.ul}}. {{de.aircraftClass.hint}} Fehlt sie bei einem älteren eigenen Muster, steht oben in der Flotte ein Hinweis mit einem Knopf je Muster; dort lässt sie sich nachtragen.',
      },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: '{{mt.web.hint}}' }] },
    ],
    see: ['rolle-verwaltung', 'papierkorb'],
  },
  {
    id: 'einstellungen',
    title: 'Einstellungen des Vereins',
    summary: 'Mitwirkung der Mitglieder, Foto-Pflicht, Nähe-Radius, Sichtungsfrist und Sperren.',
    group: 'tasks',
    roles: ['admin'],
    keywords: ['Radius', 'Frist', 'Schalter', 'Mitwirkung', 'Foto', 'Pflicht', 'Sperren', 'gesperrt'],
    blocks: [
      { p: 'Unter {{de.nav.admin}} → {{de.admin.settings}}; jede Änderung gilt sofort für den ganzen Verein:' },
      {
        list: [
          '„{{de.participation.title}}“: drei Stufen, siehe eigenes Thema.',
          '„{{de.media.requiredTitle}}“: {{de.media.requiredHint}} Der Server prüft das auch, eine ältere App kann es also nicht umgehen. Kommt das Foto einer Meldung noch vom Handy (ohne Netz gemacht), steht im Eingang so lange „{{de.media.pending}}“.',
          '„{{tx.admin.settings.radius}}“: {{tx.admin.settings.radiusHint}} Die Vorschau zeigt den Umkreis auf einer Zeichnung. Neue Vereine starten mit {{settings.duplicateRadiusPercent}} %.',
          '„{{tx.admin.settings.reviewDue}}“: {{tx.admin.settings.reviewDueHint}} Neue Vereine starten mit {{settings.reviewDueDays}} Tagen.',
          '„{{de.notices.groundingTitle}}“: {{de.notices.groundingHint}} Neue Vereine starten ohne.',
        ],
      },
    ],
    see: ['mitwirkung', 'naehe-warnung', 'eingang', 'hinweise-der-technik'],
  },
  {
    id: 'mitwirkung',
    title: 'Mitwirkung der Mitglieder',
    summary: 'Was Mitglieder in deinem Verein schreiben dürfen.',
    group: 'tasks',
    roles: ['member', 'admin'],
    keywords: ['melden abgeschaltet', 'nur lesen', 'nur Rückfragen', 'Hinweise', 'darf nicht melden'],
    blocks: [
      { p: '{{de.participation.intro}} Die Verwaltung wählt unter {{de.nav.admin}} → {{de.admin.settings}} eine von drei Stufen:' },
      {
        list: [
          '„{{de.participation.level.report}}“ (so starten neue Vereine): {{de.participation.explain.report}}',
          '„{{de.participation.level.comment}}“: {{de.participation.explain.comment}}',
          '„{{de.participation.level.read}}“: {{de.participation.explain.read}}',
        ],
      },
      { p: 'Was nicht erlaubt ist, blendet die App aus und sagt an der Stelle, warum – etwa „{{de.participation.reportOff}}“.' },
    ],
    see: ['einstellungen', 'melden-nicht-moeglich'],
  },
  {
    id: 'papierkorb',
    title: 'Papierkorb',
    summary: 'Gelöschte Schäden, Fotos, Rückfragen/Hinweise und Flugzeuge zurückholen.',
    group: 'tasks',
    roles: ['admin'],
    keywords: ['gelöscht', 'wiederherstellen', 'zurückholen'],
    blocks: [
      {
        p: 'Löschen ist in {{de.app.name}} nie endgültig. Unter {{de.nav.admin}} → {{de.admin.trash}} stehen gelöschte Schäden, Fotos, Rückfragen/Hinweise und Flugzeuge mit dem Zeitpunkt und der Person; „{{de.damage.restore}}“ holt sie zurück.',
      },
    ],
    see: ['verlauf', 'protokoll'],
  },
  {
    id: 'protokoll',
    title: 'Protokoll',
    summary: 'Wer wann was geändert hat – im ganzen Verein.',
    group: 'tasks',
    roles: ['admin'],
    keywords: ['Änderungen', 'Audit', 'wer hat', 'Historie'],
    blocks: [
      {
        p: 'Unter {{de.nav.admin}} → {{de.admin.protocol}} steht jede Änderung im Verein. Du filterst nach „{{tx.admin.protocol.entity}}“, „{{tx.admin.protocol.actor}}“, „{{tx.admin.protocol.action}}“ und Zeitraum; ein Eintrag zeigt „{{tx.admin.protocol.before}}“ und „{{tx.admin.protocol.after}}“.',
      },
      { p: 'Das Protokoll wird nach {{retention.changeEventsMonths}} Monaten gelöscht.' },
    ],
    see: ['verlauf', 'datenschutz'],
  },
  {
    id: 'verein',
    title: 'Vereinsdaten, Logo und Export',
    summary: 'Vereinslogo, Kontaktdaten und die Kopie aller Vereinsdaten.',
    group: 'tasks',
    roles: ['admin'],
    keywords: ['Logo', 'Vereinslogo', 'Wappen', 'Kontakt', 'Export', 'ZIP', 'Download', 'DSGVO', 'Datenübertragbarkeit'],
    blocks: [
      { p: 'Unter {{de.nav.admin}} → {{tx.club.tab}}:' },
      {
        list: [
          '„{{de.logo.title}}“: {{de.logo.hint}} PNG, JPEG, WebP und SVG gehen; „{{de.logo.remove}}“ zeigt wieder das Kürzel des Vereins. Das Hochladen steht im {{de.admin.protocol}}.',
          '{{tx.club.intro}} Sie stehen auf der Datenschutzseite deines Vereins.',
          '„{{tx.export.title}}“: {{tx.export.intro}} Exportdateien werden nach {{retention.exportDays}} Tagen gelöscht; ein Download-Link gilt eine Stunde und lässt sich neu anfordern. Jeder Export steht im {{de.admin.protocol}}.',
        ],
      },
    ],
    see: ['datenschutz', 'anmelden'],
  },
  {
    id: 'vereine-wechseln',
    title: 'Mehrere Vereine',
    summary: 'Wer in mehreren Vereinen ist, wechselt mit einem Tipp.',
    group: 'tasks',
    keywords: ['Verein wechseln', 'zweiter Verein', 'neuer Verein', 'Einladung', 'Rolle'],
    blocks: [
      { p: 'Deine Rolle gilt je Verein: In einem kannst du {{role.editor}} sein, im anderen {{role.member}}. Schäden, Nummern und Einstellungen gehören immer zum gerade gewählten Verein.' },
      { p: 'Hat ein Verein ein Logo hinterlegt, steht es in der Auswahl und oben neben dem Vereinsnamen; sonst das Kürzel des Vereins.' },
      { on: ['web'], blocks: [{ p: 'Der Vereinsname in der Kopfzeile öffnet die Auswahl. Die Wahl merkt sich der Browser.' }] },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: 'Tippe unter {{de.nav.fleet}} oben auf den Vereinsnamen (mit ▾) – oder unter {{de.nav.profile}} auf „{{tx.tenant.switcher}}“. Beides gibt es erst ab zwei Vereinen.' }, { p: 'Hat dich ein weiterer Verein aufgenommen, erscheint er, sobald du die App wieder öffnest oder unter {{de.nav.fleet}} die Liste nach unten ziehst – ohne neue Anmeldung.' }] },
    ],
    see: ['kein-verein'],
  },
  {
    id: 'profilbild',
    title: '{{de.avatar.title}}',
    summary: 'Ein kleines Bild neben deinem Namen – freiwillig.',
    group: 'tasks',
    keywords: ['Profilbild', 'Foto', 'Avatar', 'Bild', 'Initialen'],
    blocks: [
      { p: 'Unter {{de.nav.profile}} wählst du ein Bild aus. {{de.avatar.hint}}' },
      { p: 'Es erscheint neben deinem Namen: bei deinen Meldungen und Rückfragen/Hinweisen, im {{de.damage.history}} und in der Mitgliederliste. Ohne Bild stehen dort deine Initialen.' },
      { p: 'Vor dem Hochladen wählst du den quadratischen Ausschnitt: zoomen und verschieben, bis das Gesicht gut sitzt. Danach wird das Bild verkleinert; Standort- und Kameradaten werden entfernt. „{{de.avatar.remove}}“ löscht es wieder.' },
      { on: ['web'], blocks: [{ p: 'Im Browser: „{{de.avatar.add}}“ (oder „{{de.avatar.change}}“) unter deinem Bild im Profil. Im Fenster „{{de.avatar.cropTitle}}“ ziehst du das Bild mit Maus oder Finger an die richtige Stelle und zoomst mit dem Regler oder dem Mausrad; „{{de.avatar.apply}}“ speichert.' }] },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: 'In der App tippst du unter dem Bild auf „{{tx.create.addPhotoCamera}}“ oder „{{tx.create.addPhotoGallery}}“. Danach öffnet sich der Zuschnitt des Systems: mit zwei Fingern zoomen, mit einem verschieben, dann bestätigen. Sie fragt einmal nach dem Zugriff auf Kamera oder Fotos, wie bei den Schadensfotos.' }] },
    ],
    see: ['anmelden', 'datenschutz'],
  },
  {
    id: 'weitere-adressen',
    title: '{{tx.profile.emails.title}}',
    summary: 'Mit mehr als einer E-Mail-Adresse anmelden – etwa privat und dienstlich.',
    group: 'tasks',
    keywords: ['E-Mail', 'Adresse', 'zweite Adresse', 'weitere Adresse', 'Bestätigungscode', 'Anmeldung'],
    blocks: [
      { p: 'Unter {{de.nav.profile}} → {{tx.profile.emails.title}} stehen die Adressen, mit denen du dich anmelden kannst. {{tx.profile.emails.hint}}' },
      { p: '{{tx.profile.emails.primaryHint}} Ändern kann sie die Verwaltung deines Vereins.' },
      { h: 'Eine Adresse hinzufügen' },
      {
        steps: [
          '„{{tx.profile.emails.add}}“: Adresse eingeben und „{{tx.profile.emails.send}}“ wählen.',
          'An diese Adresse kommt ein Bestätigungscode aus {{limits.loginCodeLength}} Ziffern. Er gilt {{limits.loginCodeMinutes}} Minuten und erlaubt {{limits.loginCodeAttempts}} Versuche.',
          'Den Code unter „{{tx.profile.emails.codeLabel}}“ eingeben und „{{tx.profile.emails.confirm}}“ wählen. Erst dann gilt die Adresse.',
        ],
      },
      {
        note: 'Aus Datenschutzgründen verrät {{de.app.name}} nicht, ob eine Adresse schon jemand anderem gehört – dann kommt einfach kein Code. Möglich sind höchstens {{limits.maxAdditionalEmails}} weitere Adressen; Codes gehen so sparsam hinaus wie bei der Anmeldung ({{limits.loginCodesPerWindow}} in {{limits.loginCodeWindowMinutes}} Minuten, {{limits.loginCodesPerDay}} am Tag).',
      },
      { h: 'Eine Adresse entfernen' },
      { p: '„{{tx.profile.emails.remove}}“ nimmt eine weitere Adresse heraus, auch eine, die dein Verein eingetragen hat. Sie gilt sofort nicht mehr: Wer mit ihr angemeldet ist, wird abgemeldet. Anmeldungen über deine anderen Adressen bleiben.' },
      { p: 'Die Verwaltung deiner Vereine sieht deine weiteren Adressen in der Mitgliederliste. Bist du nur in einem Verein, kann sie dort auch selbst welche eintragen oder entfernen.' },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: 'In der App stehen deine Adressen im Profil. Hinzufügen und entfernen geht in der Web-App.' }] },
    ],
    see: ['anmelden', 'mitglieder', 'datenschutz'],
  },
  {
    id: 'offline',
    title: 'Ohne Netz am Flugplatz',
    summary: 'Was ohne Verbindung geht – in der App und im Browser.',
    group: 'tasks',
    keywords: ['offline', 'Hangar', 'kein Empfang', 'Warteschlange', 'ausstehend', 'senden', 'Hintergrund', 'App geschlossen', 'Funkloch'],
    blocks: [
      {
        on: ['iphone', 'ipad', 'android'],
        blocks: [
          {
            p: 'Die App behält die zuletzt geladenen Daten deines Vereins auf dem Gerät, höchstens {{limits.offlineCacheDays}} Tage seit dem letzten Abruf. Ohne Verbindung steht oben „{{mt.offline.banner}}“; Flotte, Lageplan, Schäden und die Nähe-Warnung funktionieren weiter.',
          },
          { h: '{{mt.outbox.title}}' },
          {
            list: [
              'Eine Meldung ohne Netz wird mit ihren Fotos auf dem Gerät gespeichert: „{{mt.outbox.queued}}“',
              'Auf dem Lageplan erscheint sie als gestrichelter Punkt („{{mt.outbox.pending}}“), unter {{de.nav.fleet}} steht, wie viele warten.',
              'Die App versucht es nach {{limits.outboxFirstRetrySeconds}} Sekunden wieder, dann in wachsenden Abständen bis höchstens {{limits.outboxMaxRetryMinutes}} Minuten – und sofort, wenn das Netz zurück ist. „{{mt.outbox.retryNow}}“ unter {{de.nav.profile}} stößt es von Hand an.',
              '{{mt.outbox.backgroundHint}} Ist die Meldung angekommen, sagt es eine Mitteilung „{{mt.outbox.sentInBackgroundTitle}}“ – wenn du Mitteilungen erlaubt hast. Ein Video, dessen Hochladen schon begonnen hat, lädt ohnehin im Hintergrund weiter.',
              'Unter {{de.nav.profile}} steht bei wartenden Meldungen, wann das System die App zuletzt im Hintergrund hat senden lassen und was dabei herauskam – oder dass es das noch gar nicht getan hat.',
              'Auf iPhone und iPad gilt zusätzlich: Wischst du die App in der App-Übersicht weg, lässt iOS sie bis zum nächsten Öffnen gar nicht mehr im Hintergrund arbeiten.',
              'Gesendet wird nur unter deinem Namen: Hast du dich abgemeldet, wartet die Meldung, bis du wieder angemeldet bist.',
              'Lehnt der Server eine Meldung ab, steht sie als „{{mt.outbox.refused}}“ mit Grund unter {{de.nav.profile}} und wird nicht wiederholt; „{{mt.outbox.discard}}“ entfernt sie.',
            ],
          },
          { p: 'Nur Meldungen warten auf Netz. Rückfragen/Hinweise, Statusänderungen und Sichtungen brauchen eine Verbindung.' },
        ],
      },
      {
        on: ['web'],
        blocks: [
          {
            p: 'Im Browser bleibt sichtbar, was schon geladen war, solange die Seite offen ist; oben steht dann „{{tx.app.offline}}“ mit der Uhrzeit des letzten Abrufs. Speichern geht nur mit Verbindung („{{de.errors.offline}}“). Wer im Hangar ohne Netz melden will, nimmt die App.',
          },
        ],
      },
    ],
    see: ['offline-gespeicherter-stand', 'schaden-melden'],
  },
  {
    id: 'darstellung',
    title: 'Darstellung: Sonne und Dunkelheit',
    summary: 'Hoher Kontrast für draußen und dunkle Darstellung.',
    group: 'tasks',
    keywords: ['Kontrast', 'Sonne', 'dunkel', 'Dark Mode', 'lesbar'],
    blocks: [
      { p: 'Hell oder dunkel folgt der Einstellung deines Geräts; im Dunkeln wird die Zeichnung zur hellen Linie auf Blau.' },
      { on: ['web'], blocks: [{ p: 'Unter {{de.nav.profile}} → „{{tx.profile.display}}“ schaltet „{{tx.profile.highContrast}}“ kräftigere Linien und Farben ein. {{tx.profile.highContrastHint}}' }] },
    ],
  },
  {
    id: 'datenschutz',
    title: 'Datenschutz in Kürze',
    summary: 'Was gespeichert wird und wie lange.',
    group: 'tasks',
    keywords: ['DSGVO', 'Daten', 'Löschung', 'Fristen', 'Tracking'],
    blocks: [
      { p: '{{tx.login.privacy}}' },
      {
        list: [
          'Fotos: ohne Standort- und Kameradaten.',
          'Änderungsprotokoll: {{retention.changeEventsMonths}} Monate.',
          'Anmeldeprotokoll (Adressen nur als Prüfsumme): {{retention.loginEventsMonths}} Monate.',
          'Vereinsexporte: {{retention.exportDays}} Tage.',
        ],
      },
      { p: 'Verantwortlich für die Daten ist dein Verein. Die vollständige Erklärung mit allen Fristen steht unter „{{tx.privacy.title}}“ (im Browser in der Fußzeile, in der App unter {{de.nav.profile}}).' },
    ],
    see: ['verein'],
  },
  {
    id: 'neuigkeiten',
    title: 'Neuigkeiten',
    summary: 'Was sich an {{de.app.name}} geändert hat.',
    group: 'start',
    keywords: ['neu', 'Update', 'Version', 'Änderungen', 'Changelog'],
    blocks: [
      {
        p: 'Nach einer Aktualisierung zeigt {{de.app.name}} beim nächsten Öffnen, was neu ist – eine Karte je Neuerung. Erst mit „{{tx.news.dismiss}}“ auf der letzten Karte gilt alles als gelesen, in der App wie im Browser. Hier stehen alle Neuigkeiten zum Nachlesen.',
      },
    ],
  },

  // ------------------------------------------------------------------ roles
  {
    id: 'rolle-mitglied',
    title: 'Als Mitglied',
    summary: 'Nachsehen, melden, nachfragen.',
    group: 'roles',
    roles: ['member'],
    blocks: [
      {
        list: [
          'Vor dem Flug: am Lageplan prüfen, ob ein Schaden schon bekannt ist.',
          'Etwas Neues gefunden: melden, mit Übersichts- und Detailfoto.',
          'Schon erfasst, aber anders: im Schaden eine Rückfrage/einen Hinweis schreiben.',
          'Unter „{{tx.profile.myReports}}“ verfolgen, was die Technik daraus gemacht hat.',
        ],
      },
      { p: 'Was Mitglieder schreiben dürfen, legt dein Verein fest ({{de.participation.title}}).' },
    ],
    see: ['schaden-melden', 'naehe-warnung', 'rueckfragen', 'meine-meldungen', 'mitwirkung'],
  },
  {
    id: 'rolle-technik',
    title: 'Als Technik',
    summary: 'Meldungen sichten, Schäden pflegen.',
    group: 'roles',
    roles: ['editor'],
    blocks: [
      {
        list: [
          'Im {{de.nav.inbox}} neue Meldungen bestätigen, zusammenführen oder verwerfen.',
          'Schäden selbst erfassen – mit Status, ohne Sichtung.',
          'Den Status bis „{{status.behoben}}“ pflegen.',
          'Rückfragen/Hinweise der Mitglieder beantworten.',
        ],
      },
      { p: 'Die Mitwirkungsstufe des Vereins schränkt Technik nie ein.' },
    ],
    see: ['eingang', 'status-aendern', 'rueckfragen'],
  },
  {
    id: 'rolle-verwaltung',
    title: 'Als Verwaltung',
    summary: 'Mitglieder, Flotte, Einstellungen, Protokoll und Papierkorb.',
    group: 'roles',
    roles: ['admin'],
    blocks: [
      { p: 'Als {{role.admin}} kannst du alles, was die Technik kann, und dazu unter {{de.nav.admin}}:' },
      {
        list: [
          '{{de.admin.members}}: wer sich anmelden darf und mit welcher Rolle.',
          '{{de.admin.fleet}}: Flugzeuge und Muster mit ihren Zeichnungen.',
          '{{de.admin.settings}}: Mitwirkung der Mitglieder, Nähe-Radius, Sichtungsfrist.',
          '{{de.admin.protocol}} und {{de.admin.trash}}: nachvollziehen und zurückholen.',
          '{{tx.club.tab}}: Kontaktdaten und Export.',
        ],
      },
      { on: ['iphone', 'ipad', 'android'], blocks: [{ p: '{{mt.web.hint}}' }] },
    ],
    see: ['mitglieder', 'flotte-verwalten', 'einstellungen', 'protokoll', 'papierkorb', 'verein'],
  },

  // ------------------------------------------------------------------ devices
  {
    id: 'iphone',
    title: 'iPhone',
    summary: 'Die App auf dem iPhone: Kamera, Fotos, Zurück-Geste.',
    group: 'devices',
    device: 'iphone',
    keywords: ['iOS', 'Apple', 'Berechtigung', 'Einstellungen'],
    blocks: [
      { p: 'Unten wechselst du zwischen {{de.nav.fleet}}, {{de.nav.search}}, {{de.nav.inbox}} (nur Technik und Verwaltung) und {{de.nav.profile}}. Zurück geht es oben links oder mit einem Wisch vom linken Rand.' },
      { h: 'Kamera und Fotos erlauben' },
      {
        p: 'Beim ersten Foto fragt das iPhone nach dem Zugriff. Hast du abgelehnt, sagt die App „{{mt.photos.cameraDenied}}“. So erlaubst du es nachträglich: Einstellungen → Apps → {{de.app.name}} → Kamera bzw. Fotos.',
      },
      { h: 'Ohne Netz' },
      { p: 'Lesen und Melden gehen weiter, siehe „Ohne Netz am Flugplatz“.' },
      { h: 'Dunkle Darstellung' },
      { p: 'Die App folgt Einstellungen → Anzeige & Helligkeit.' },
      { note: 'Ein offenes Meldeformular schließt du mit dem ✕; was du eingegeben hast, ist dann verworfen.' },
    ],
    see: ['offline', 'fotos', 'foto-abgelehnt'],
  },
  {
    id: 'ipad',
    title: 'iPad',
    summary: 'Die App auf dem iPad und iPad mini: Hoch- und Querformat, neben anderen Apps.',
    group: 'devices',
    device: 'ipad',
    keywords: ['iPadOS', 'iPad mini', 'Tablet', 'Querformat', 'Hochformat', 'Split View', 'Slide Over', 'nebeneinander'],
    blocks: [
      { p: 'Die App läuft auf dem iPad und dem iPad mini im Hoch- und im Querformat. Unter {{de.nav.fleet}} stehen so viele Flugzeuge nebeneinander, wie Platz haben; Texte bleiben lesbar schmal.' },
      {
        p: 'Im Lageplan steht die Liste der Schäden auf der Seite statt hinter einem Knopf: im Hochformat unter der Zeichnung, im Querformat rechts daneben – dort zusammen mit den {{de.notices.title}}, damit die Zeichnung die volle Höhe behält. Ein Finger verschiebt, zwei zoomen; Antippen setzt das Kreuz, gedrückt halten zeigt die Lupe.',
      },
      { p: 'Ein Schaden zeigt sich auf breitem Bildschirm in zwei Spalten: links Fotos, Beschreibung, Rückfragen und Verlauf, rechts Angaben, Lage und die Schritte der Technik.' },
      { h: 'Neben einer anderen App' },
      { p: 'In Split View oder Slide Over passt sich die App der Breite an: in einer schmalen Spalte sieht sie aus wie auf dem iPhone, die Liste im Lageplan liegt dann wieder hinter „{{tx.plan.listToggle}}“.' },
      { h: 'Kamera und Fotos erlauben' },
      { p: 'Wie auf dem iPhone: Einstellungen → Apps → {{de.app.name}} → Kamera bzw. Fotos.' },
    ],
    see: ['iphone', 'web'],
  },
  {
    id: 'android',
    title: 'Android',
    summary: 'Die App auf Android: Zurück-Geste, Kamera, Fotoauswahl.',
    group: 'devices',
    device: 'android',
    keywords: ['Samsung', 'Pixel', 'Google', 'Berechtigung', 'Zurück', 'Tablet', 'Querformat', 'Hochformat'],
    blocks: [
      { p: 'Unten wechselst du zwischen {{de.nav.fleet}}, {{de.nav.search}}, {{de.nav.inbox}} (nur Technik und Verwaltung) und {{de.nav.profile}}.' },
      { h: 'Tablet' },
      {
        p: 'Auf einem Android-Tablet läuft die App im Hoch- und im Querformat, auch geteilt neben einer anderen App; auf dem Telefon bleibt sie im Hochformat. Auf breitem Bildschirm steht im Lageplan die Liste der Schäden auf der Seite – im Querformat rechts neben der Zeichnung, zusammen mit den {{de.notices.title}}.',
      },
      { h: 'Zurück-Geste und Zurück-Taste' },
      {
        p: 'Die Zurück-Geste (Wisch vom Rand) oder die Zurück-Taste schließt ein offenes Fenster, etwa das Meldeformular oder die Liste – was du dort eingegeben hast, ist dann verworfen. Sonst geht sie eine Seite zurück.',
      },
      { h: 'Kamera und Fotos' },
      {
        p: 'Für die Kamera fragt Android einmal nach der Erlaubnis. Fotos wählst du in der Fotoauswahl des Systems; dafür braucht {{de.app.name}} keinen Zugriff auf deine Galerie. Kamera nachträglich erlauben: Einstellungen → Apps → {{de.app.name}} → Berechtigungen → Kamera.',
      },
      { h: 'Ohne Netz' },
      { p: 'Lesen und Melden gehen weiter, siehe „Ohne Netz am Flugplatz“.' },
    ],
    see: ['offline', 'fotos'],
  },
  {
    id: 'web',
    title: 'Im Browser',
    summary: 'Auf jedem Gerät, auch als Symbol auf dem Home-Bildschirm.',
    group: 'devices',
    device: 'web',
    keywords: ['Browser', 'Safari', 'Chrome', 'Edge', 'Firefox', 'PWA', 'installieren', 'Home-Bildschirm', 'Computer'],
    blocks: [
      {
        p: '{{de.app.name}} läuft in jedem aktuellen Browser. Auf dem Computer steht die Navigation links, auf dem Handy unten. Die Verwaltung ({{de.admin.members}}, {{de.admin.fleet}}, {{de.admin.settings}} …) gibt es nur hier.',
      },
      { h: 'Auf den Home-Bildschirm legen' },
      {
        list: [
          'iPhone und iPad (Safari): Teilen-Symbol → „Zum Home-Bildschirm“.',
          'Android (Chrome): Menü ⋮ → „App installieren“ bzw. „Zum Startbildschirm hinzufügen“.',
          'Computer (Chrome, Edge): das Installieren-Symbol rechts in der Adresszeile.',
        ],
      },
      { p: 'Vom Home-Bildschirm geöffnet läuft {{de.app.name}} ohne Adresszeile wie eine App; Anmeldung und Daten sind dieselben.' },
      { h: 'Neue Version' },
      {
        p: 'Eine geöffnete Seite fragt alle paar Minuten und beim Zurückkehren nach, ob es eine neuere Version gibt. Dann steht oben „{{de.update.available}}“; „{{de.update.reload}}“ lädt sie. Von selbst lädt die Seite nie neu – schließe vorher ab, was du gerade einträgst.',
      },
      { h: 'Kamera und Fotos' },
      {
        p: '„{{tx.create.addPhotoCamera}}“ öffnet die Kamera des Geräts, „{{tx.create.addPhotoGallery}}“ die Dateiauswahl. Fragt der Browser nach der Kamera, erlaube es. Zurücknehmen kannst du das in den Website-Einstellungen des Browsers (Schloss-Symbol neben der Adresse), auf dem iPhone unter Einstellungen → Apps → Safari → Kamera.',
      },
      { h: 'Ohne Netz' },
      { p: 'Was geladen ist, bleibt sichtbar, Speichern braucht aber eine Verbindung. Siehe „Ohne Netz am Flugplatz“.' },
      { h: 'Tastatur' },
      { p: 'Auf dem Lageplan: {{tx.plan.keyboardHint}}' },
    ],
    see: ['offline', 'fotos', 'darstellung'],
  },
  {
    id: 'benachrichtigungen',
    title: '{{de.notifications.title}}',
    summary: 'Hinweise aufs Telefon bei neuen Meldungen und zu deinen eigenen.',
    group: 'devices',
    requires: 'push',
    keywords: ['Push', 'Mitteilungen', 'Hinweis', 'Benachrichtigung', 'stumm'],
    blocks: [
      { p: '{{de.notifications.appOnly}} {{mt.push.explainBody}}' },
      { p: '{{mt.push.explainPrivacy}}' },
      { h: 'Ein- und ausschalten' },
      {
        list: [
          'Beim ersten Anmelden in der App erklärt sie kurz, worum es geht; erst „{{mt.push.allow}}“ fragt das Telefon nach der Erlaubnis.',
          'Unter {{de.nav.profile}} → „{{de.notifications.title}}“ wählst du je Verein: „{{de.notifications.ownReports}}“ ({{de.notifications.ownReportsHint}}) und – für Technik und Verwaltung – „{{de.notifications.newReports}}“ ({{de.notifications.newReportsHint}}).',
          'Hast du die Erlaubnis abgelehnt, steht dort „{{mt.push.off}}“ mit „{{mt.push.turnOn}}“.',
        ],
      },
      {
        on: ['iphone', 'ipad'],
        blocks: [{ p: 'Erlauben oder abschalten auch unter Einstellungen → Mitteilungen → {{de.app.name}}.' }],
      },
      {
        on: ['android'],
        blocks: [{ p: 'Erlauben oder abschalten auch unter Einstellungen → Apps → {{de.app.name}} → Benachrichtigungen.' }],
      },
      { p: '{{mt.push.demo}} Im Browser gibt es keine.' },
    ],
    see: ['iphone', 'android'],
  },

  // ------------------------------------------------------------------ faq
  {
    id: 'code-kommt-nicht-an',
    title: 'Der Code kommt nicht an',
    summary: 'Spam-Ordner, web.de und GMX, falsche Adresse.',
    group: 'faq',
    keywords: ['E-Mail', 'Spam', 'web.de', 'GMX', 'Code', 'Mail fehlt'],
    blocks: [
      {
        list: [
          'Sieh im Spam- bzw. Werbung-Ordner nach. Der Betreff lautet „{{de.app.name}} – Anmeldecode …“.',
          'Bei web.de und GMX kann die Mail sich verspäten oder von deren Server abgewiesen werden. Warte ein paar Minuten, statt den Code mehrfach anzufordern – jede neue Anforderung ersetzt den alten Code.',
          'Ist es die Adresse, die dein Verein für dich hinterlegt hat? Die Anmeldung verrät aus Datenschutzgründen nicht, ob eine Adresse bekannt ist. Im Zweifel frag die Verwaltung deines Vereins.',
        ],
      },
      { p: 'Ein Code gilt {{limits.loginCodeMinutes}} Minuten. Nach {{limits.loginCodesPerWindow}} Codes in {{limits.loginCodeWindowMinutes}} Minuten musst du kurz warten.' },
    ],
    see: ['anmelden'],
  },
  {
    id: 'foto-abgelehnt',
    title: 'Ein Foto wird abgelehnt',
    summary: 'Wegen Metadaten, Größe oder weil schon {{limits.maxPhotosPerDamage}} Fotos dran sind.',
    group: 'faq',
    keywords: ['Foto', 'abgelehnt', 'Metadaten', 'EXIF', 'zu groß', 'Fehler'],
    blocks: [
      {
        list: [
          '„{{mt.photos.stillHasMetadata}}“ – die App entfernt Standort- und Kameradaten, bevor ein Foto das Gerät verlässt. Gelingt das bei einer Datei nicht, verwendet sie das Foto nicht. Nimm es neu mit „{{tx.create.addPhotoCamera}}“ auf.',
          'Ein Schaden hat schon {{limits.maxPhotosPerDamage}} Fotos: Entferne eines, bevor du ein neues hinzufügst.',
          '„{{tx.create.photoFailed}}“ – das Format konnte nicht gelesen werden. Fotografiere die Stelle neu.',
        ],
      },
      { on: ['web'], blocks: [{ p: 'Im Browser bleibt ein abgelehntes Foto mit dem Grund im Formular; die übrigen sind gespeichert. {{tx.upload.retryHint}}' }] },
    ],
    see: ['fotos'],
  },
  {
    id: 'offline-gespeicherter-stand',
    title: '„{{mt.offline.banner}}“',
    summary: 'Warum die App diesen Hinweis zeigt und was du tun kannst.',
    group: 'faq',
    keywords: ['offline', 'Stand', 'alt', 'aktualisieren', 'Banner'],
    blocks: [
      {
        p: 'Die App erreicht den Server gerade nicht und zeigt die Daten, die sie zuletzt geladen hat. Was andere seitdem gemeldet haben, siehst du erst wieder mit Verbindung.',
      },
      {
        list: [
          'Melden geht weiter; die Meldung wartet in der {{mt.outbox.title}}.',
          'Sobald wieder Netz da ist, verschwindet der Hinweis von selbst. Auf den Listen kannst du zum Aktualisieren nach unten ziehen.',
          'Die gespeicherten Daten hält die App höchstens {{limits.offlineCacheDays}} Tage seit dem letzten Abruf.',
        ],
      },
      { on: ['web'], blocks: [{ p: 'Im Browser heißt der Hinweis „{{tx.app.offline}}“, mit der Uhrzeit des letzten Abrufs dahinter.' }] },
    ],
    see: ['offline'],
  },
  {
    id: 'verein-anmelden',
    title: 'Verein anmelden',
    summary: 'Wie ein Verein {{de.app.name}} online bestellt und sofort loslegt.',
    group: 'faq',
    keywords: ['Vertrag', 'bestellen', 'Anmeldung', 'Preis', 'Pionierverein', 'Zugang'],
    blocks: [
      {
        p: 'Ein Verein schließt den Vertrag selbst ab, unter {{tx.signup.title}} (Adresse /verein-anmelden). Das macht eine Person, die den Verein vertreten darf – meist jemand aus dem Vorstand.',
      },
      {
        steps: [
          'Gib die Daten des Vereins, deine eigenen und eure Flotte ein; der Preis rechnet sich dabei mit.',
          'Prüfe die Zusammenfassung, stimme AGB und Auftragsverarbeitung zu und tippe auf „{{tx.signup.submit}}“.',
          'Du bekommst einen Bestätigungscode per E-Mail. Er gilt {{limits.signupCodeMinutes}} Minuten; „{{tx.signup.resend}}“ schickt einen neuen.',
          'Mit „{{tx.signup.confirm}}“ ist der Vertrag geschlossen. Euer Vereinsbereich ist sofort eingerichtet, und du bist dort Verwaltung.',
        ],
      },
      {
        p: 'Wird der Code nicht innerhalb von {{limits.signupPendingHours}} Stunden eingegeben, verfällt die Anmeldung, und es entsteht kein Vertrag. Die Vertragsbestätigung mit AGB und Auftragsverarbeitungsvertrag kommt per E-Mail.',
      },
      {
        p: 'Es gibt {{limits.founderPlaces}} Pionierplätze mit {{limits.founderDiscountPercent}} % Nachlass. Solange einer frei ist, bekommt ihn euer Verein beim Abschluss automatisch.',
      },
    ],
    see: ['rolle-verwaltung', 'mitglieder', 'flotte-verwalten'],
  },
  {
    id: 'kein-verein',
    title: '„{{tx.tenant.noneTitle}}“',
    summary: 'Angemeldet, aber kein Verein zu sehen.',
    group: 'faq',
    keywords: ['kein Verein', 'nicht freigeschaltet', 'leer', 'Zugang'],
    blocks: [
      { p: '{{tx.tenant.noneBody}}' },
      { p: 'Das passiert, wenn deine Mitgliedschaft deaktiviert wurde oder der Verein {{de.app.name}} nicht mehr nutzt.' },
    ],
    see: ['anmelden', 'vereine-wechseln'],
  },
  {
    id: 'melden-nicht-moeglich',
    title: 'Ich kann nicht melden oder nichts schreiben',
    summary: 'Meist hat der Verein die Mitwirkung der Mitglieder eingeschränkt.',
    group: 'faq',
    keywords: ['kann nicht melden', 'Knopf fehlt', 'nicht erlaubt', 'Berechtigung'],
    blocks: [
      {
        list: [
          '„{{de.participation.reportOff}}“ oder „{{de.participation.commentOff}}“: So hat es dein Verein eingestellt. Ansprechpartner ist die Verwaltung.',
          'Eine gesichtete Meldung kannst du nicht mehr ändern – schreib stattdessen eine Rückfrage/einen Hinweis.',
          '„{{tx.errors.notAllowed}}“: Deine Rolle reicht dafür nicht.',
          '„{{tx.errors.stale}}“: Jemand hat den Schaden gerade geändert. Die Seite lädt neu; versuch es dann noch einmal.',
        ],
      },
    ],
    see: ['mitwirkung', 'rueckfragen'],
  },
];
